import { useState, useEffect, useContext } from 'react';
import type { CSSProperties, PropsWithChildren, ReactNode, Ref } from 'react';
import { Button, Modal } from 'antd';
import ProTable, { ProColumns } from '@ant-design/pro-table';
import type { ActionType } from '@ant-design/pro-table';
import type { ProFormProps, QueryFilterProps } from '@ant-design/pro-form';
import { ABM, Tools } from '../../../../shared';
import './style.less';
import ExceptionManager from '../../ExceptionManager';
import { SaveOutlined, ClearOutlined } from '@ant-design/icons';
import { PageLoading } from '@ant-design/pro-layout';
import GraphqlService from '../../../../services/graphql/GraphqlService';
import { ContextApp } from '../../../../contexts/ContextApp';

export type SaveFormColumns<T = object> = ProColumns<T> & {
  type?: string;
  export?: boolean;
};

/**
 * @property modalVisible: determina si el modal es visible o no
 * @property onCancel: funcion ejecutada al cerrar el modal
 * @property onOk: funcion ejecutada al realizar submit del formulario
 * @property columns: campos a llenar en el formulario. Especificar el tipo de columna ya que será el tipo de dato que se enviará en la función on ok
 * @property title: titulo del modal
 * @property className: clases extras para estilizar el formulario de alguna forma particular. El selector a utilizar debería ser SaveForm.(clase extra)
 * @property submitText: texto a mostrar en el boton submit
 * @property buttonCancel: booleano que determina si muestra o no el boton cancelar
 * @property onReset: funcion ejecutada al limpiar el formulario
 * @property values: valores por defecto del formulario
 * @property message: mensaje a mostrar como primera línea del formulario
 * @property loading: booleano que determina si el boton submit está cargando o no
 * @property saveFormFooterIcon: desactiva o cambia los iconos actuales de los botones del footer
 */
export interface SaveFormProps<
  TColumns extends object = object,
  TSubmit extends object = object,
> extends PropsWithChildren {
  readonly modalVisible: boolean;
  readonly onCancel: () => void;
  readonly onOk?: (val: TSubmit) => void;
  readonly columns: SaveFormColumns<TColumns>[];
  readonly title: string;
  readonly className?: string;
  readonly submitText?: string;
  readonly cancelText?: string;
  readonly buttonCancel?: boolean;
  readonly onReset?: () => void;
  readonly values?: object;
  readonly message?: string;
  readonly loading?: boolean;
  readonly formItemLayout?: Omit<ProFormProps & QueryFilterProps, 'form'>;
  readonly width?: string | number;
  readonly style?: CSSProperties;
  readonly saveFormFooterIcon?: {
    readonly reset?: ReactNode;
    readonly cancel?: ReactNode;
    readonly submit?: ReactNode;
  };
  readonly disabledSubmit?: boolean;
  readonly footer?: ReactNode[];
  readonly renderAsChildren?: boolean;
  readonly actionRefProTable?: Ref<ActionType | undefined>;
  readonly notIgnoreFalsyValues?: boolean;
  readonly layoutDirection?: 'vertical' | 'horizontal' | 'inline';
}

const SaveFormContent = <TColumns extends object, TSubmit extends object>(
  props: SaveFormProps<TColumns, TSubmit>,
) => {
  const { user, t } = useContext(ContextApp);
  const [stColumns, setStColumns] = useState<SaveFormColumns<TColumns>[]>([]);
  const { rmTypenameObj } = GraphqlService();

  const initialValuesF = (set: object) => {
    const source = set as Record<string, unknown>;
    const values = rmTypenameObj(set) as Record<string, unknown>;
    Object.keys(source).forEach((key) => {
      if (Object.hasOwn(values, key)) {
        switch (typeof source[key]) {
          case 'boolean':
            values[key] = `${source[key]}`;
            break;
          case 'number':
            values[key] = `${source[key]}`;
            break;
          default:
            values[key] = source[key];
            break;
        }
      }
    });
    return values;
  };

  const {
    modalVisible,
    onCancel,
    onOk,
    columns,
    values,
    title,
    message,
    className,
    submitText,
    cancelText,
    buttonCancel,
    onReset,
    loading,
    saveFormFooterIcon,
    formItemLayout,
    width,
    style,
    disabledSubmit,
    footer,
    renderAsChildren,
    actionRefProTable,
    notIgnoreFalsyValues,
    layoutDirection = 'vertical',
  } = props;

  const searchConfig = {
    searchText: submitText || Tools.capitalize(t('action.save')),
    resetText:
      cancelText ??
      (values
        ? Tools.capitalize(t('action.undo'))
        : Tools.capitalize(t('action.clean'))),
  };

  const setColumns = () =>
    setStColumns(
      columns.map((column) => {
        const formColumn = { ...column };
        delete formColumn.export;
        delete formColumn.sorter;
        return formColumn;
      }),
    );

  useEffect(() => {
    setColumns();
  }, [columns]);

  const layout = formItemLayout;

  const proTableComponent = () => (
    <>
      {message && <p>{message}</p>}
      {stColumns.length ? (
        <ProTable
          onSubmit={(value) => {
            if (onOk) {
              onOk(
                ABM.parseObjByConfigColumn(
                  columns,
                  value,
                  notIgnoreFalsyValues ?? false,
                  user,
                ) as TSubmit,
              );
            }
          }}
          rowKey="id"
          search={searchConfig}
          actionRef={actionRefProTable}
          type="form"
          form={{
            ...layout,
            initialValues: values ? initialValuesF(values) : {},
            layout: layoutDirection,
            submitter: {
              render: (props) => (
                <div key="SaveFormFooter" className="SaveFormFooter">
                  {footer?.length ? (
                    <>{footer}</>
                  ) : (
                    <>
                      <Button
                        htmlType="reset"
                        className="Reset"
                        onClick={() => {
                          props.form?.resetFields();
                          if (onReset) {
                            onReset();
                          }
                        }}
                        icon={
                          saveFormFooterIcon ? (
                            saveFormFooterIcon?.reset || null
                          ) : (
                            <ClearOutlined />
                          )
                        }
                        disabled={loading}
                      >
                        {searchConfig.resetText}
                      </Button>
                      {buttonCancel ? (
                        <Button
                          className="Cancel"
                          disabled={loading}
                          onClick={() => onCancel()}
                          icon={saveFormFooterIcon?.cancel}
                        >
                          {Tools.capitalize(t('action.cancel'))}
                        </Button>
                      ) : null}
                      <Button
                        htmlType="submit"
                        className="Submit"
                        type="primary"
                        loading={loading}
                        disabled={disabledSubmit}
                        icon={
                          saveFormFooterIcon ? (
                            saveFormFooterIcon?.submit || null
                          ) : (
                            <SaveOutlined />
                          )
                        }
                      >
                        {searchConfig.searchText}
                      </Button>
                    </>
                  )}
                </div>
              ),
            },
          }}
          columns={stColumns}
        />
      ) : (
        <PageLoading />
      )}
    </>
  );

  return renderAsChildren ? (
    proTableComponent()
  ) : (
    <Modal
      destroyOnHidden
      title={title}
      open={modalVisible}
      onCancel={() => onCancel()}
      footer={null}
      className={['SaveForm', className].filter(Boolean).join(' ')}
      width={width}
      style={style}
    >
      {proTableComponent()}
    </Modal>
  );
};

const SaveForm = <TColumns extends object, TSubmit extends object>(
  props: SaveFormProps<TColumns, TSubmit>,
) => {
  const { t } = useContext(ContextApp);

  return (
    <ExceptionManager messageModal={t('error.saveform')}>
      <SaveFormContent {...props} />
    </ExceptionManager>
  );
};

export default SaveForm;
